import React, { useEffect, useRef } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Check, CheckCheck } from 'lucide-react';

const MensagensContainer = ({ mensagens, userId, digitando, nomeDigitando }) => {
  const containerRef = useRef(null);
  const lastMessageRef = useRef(null);

  useEffect(() => {
    if (lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [mensagens, digitando]);

  const formatarHora = (data) => {
    return format(new Date(data), 'HH:mm', { locale: ptBR });
  };

  const formatarDataSeparador = (data) => {
    const date = new Date(data);
    const hoje = new Date();
    const ontem = new Date(hoje);
    ontem.setDate(ontem.getDate() - 1);

    if (date.toDateString() === hoje.toDateString()) {
      return 'Hoje';
    } else if (date.toDateString() === ontem.toDateString()) {
      return 'Ontem';
    } else {
      return format(date, "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
    }
  };

  const mensagensAgrupadas = mensagens.reduce((acc, mensagem) => {
    const data = new Date(mensagem.createdAt).toDateString();
    if (!acc[data]) {
      acc[data] = [];
    }
    acc[data].push(mensagem);
    return acc;
  }, {});

  if (mensagens.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center text-gray-400 dark:text-dark-muted">
          <div className="text-4xl mb-2">💬</div>
          <p className="text-sm">Nenhuma mensagem ainda</p>
          <p className="text-xs mt-1">Seja o primeiro a enviar uma mensagem!</p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 dark:bg-dark-bg"
    >
      {Object.entries(mensagensAgrupadas).map(([data, msgs], groupIndex) => (
        <div key={data}>
          {/* Separador de Data */}
          <div className="flex items-center justify-center mb-4">
            <div className="bg-gray-200 dark:bg-dark-border text-gray-600 dark:text-dark-muted text-xs px-3 py-1 rounded-full">
              {formatarDataSeparador(msgs[0].createdAt)}
            </div>
          </div>

          {/* Mensagens do dia */}
          {msgs.map((mensagem, index) => {
            const enviada = mensagem.remetenteId === userId;
            const isLastMessage =
              groupIndex === Object.keys(mensagensAgrupadas).length - 1 &&
              index === msgs.length - 1;

            return (
              <div
                key={mensagem._id || index}
                ref={isLastMessage ? lastMessageRef : null}
                className={`flex mb-3 ${
                  enviada ? 'justify-end' : 'justify-start'
                }`}
              >
                <div
                  className={`max-w-[70%] sm:max-w-md ${
                    enviada ? 'order-1' : 'order-2'
                  }`}
                >
                  <div
                    className={`rounded-2xl px-4 py-2 shadow-sm ${
                      enviada
                        ? 'bg-primary-500 text-white rounded-br-sm'
                        : 'bg-white dark:bg-dark-card text-gray-900 dark:text-dark-text rounded-bl-sm'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap break-words">
                      {mensagem.conteudo}
                    </p>

                    <div
                      className={`flex items-center justify-end gap-1 mt-1 text-xs ${
                        enviada
                          ? 'text-white/80'
                          : 'text-gray-500 dark:text-dark-muted'
                      }`}
                    >
                      <span>{formatarHora(mensagem.createdAt)}</span>
                      {enviada && (
                        <span>
                          {mensagem.lida ? (
                            <CheckCheck className="w-3 h-3 text-blue-200" />
                          ) : (
                            <Check className="w-3 h-3" />
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ))}

      {/* Indicador de digitação */}
      {digitando && (
        <div className="flex justify-start">
          <div className="bg-white dark:bg-dark-card rounded-2xl px-4 py-3 shadow-sm">
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MensagensContainer;
